import { MENUS, SUPER_ADMIN_EMAILS } from 'constants/app-constants';

export const ADMIN_MENU_ACCESS = [
	{ key: 'dashboard', labelKey: 'navigation.dashboard' },
	{ key: 'customers', labelKey: 'navigation.customers' },
	{ key: 'bulk_upload', labelKey: 'navigation.bulk_upload' },
	{ key: 'visitors', labelKey: 'navigation.visitors' },
	{ key: 'coaches', labelKey: 'navigation.coaches' },
	{ key: 'masters', labelKey: 'navigation.masters' },
	{ key: 'payments', labelKey: 'navigation.payments' },
	{ key: 'reports', labelKey: 'navigation.reports' },
	{ key: 'outstanding', labelKey: 'navigation.outstanding' },
	{ key: 'personal_training', labelKey: 'navigation.personal_training' },
	{ key: 'products', labelKey: 'navigation.products' },
	{ key: 'product_expense', labelKey: 'navigation.product_expense' },
	{ key: 'diet_plan', labelKey: 'navigation.diet_plan' },
	{ key: 'gym_tools', labelKey: 'navigation.gym_tools' },
	{ key: 'workout_planner', labelKey: 'navigation.workoutPlanner' },
	{ key: 'sms', labelKey: 'navigation.sms' },
];

const FEATURE_ROUTE_PATTERNS = [
	{ accessKey: 'dashboard', patterns: ['/'] },
	{ accessKey: 'customers', patterns: ['/customers', '/customer/:id', '/active-customers', '/inactive-customers'] },
	{ accessKey: 'bulk_upload', patterns: ['/bulk-upload'] },
	{ accessKey: 'visitors', patterns: ['/visitors'] },
	{ accessKey: 'personal_training', patterns: ['/personal-training'] },
	{ accessKey: 'products', patterns: ['/products'] },
	{ accessKey: 'coaches', patterns: ['/coaches'] },
	{ accessKey: 'payments', patterns: ['/payments'] },
	{ accessKey: 'outstanding', patterns: ['/outstanding'] },
	{ accessKey: 'product_expense', patterns: ['/product-expense-excel'] },
	{ accessKey: 'masters', patterns: ['/masters', '/memberships', '/services', '/users', '/role-management'] },
	{ accessKey: 'diet_plan', patterns: ['/diet-plan'] },
	{ accessKey: 'gym_tools', patterns: ['/gym-tools'] },
	{ accessKey: 'workout_planner', patterns: ['/workouts', '/workout-templates', '/workout-plan/:id', '/workout-plan/customer/:customerId', '/workout-plan/assign/:customerId'] },
	{ accessKey: 'reports', patterns: ['/reports', '/reports/:type'] },
	{ accessKey: 'sms', patterns: ['/sms', '/send-bulk-sms', '/send-single-sms', '/sms-pricing', '/sms-history'] },
];

const CONFIGURED_MENU_LOOKUP = ADMIN_MENU_ACCESS.reduce((lookup, item) => {
	lookup[item.key] = item;
	return lookup;
}, {});

export const DEFAULT_ENABLED_ADMIN_MENU_KEYS = ADMIN_MENU_ACCESS.filter((item) => !item.alwaysEnabled).map((item) => item.key);

const normalizeMenuPattern = (pattern) => {
	if (!pattern) return '/';
	const parameterIndex = pattern.indexOf('/:');
	if (parameterIndex >= 0) {
		return pattern.slice(0, parameterIndex);
	}
	return pattern;
};

const doesPathMatchPattern = (path, pattern) => {
	const normalizedPath = path || '/';
	const normalizedPattern = normalizeMenuPattern(pattern);

	if (normalizedPattern === '/') {
		return normalizedPath === '/';
	}

	return normalizedPath === normalizedPattern || normalizedPath.startsWith(`${normalizedPattern}/`);
};

const getAccessDefinitionForPath = (path) =>
	FEATURE_ROUTE_PATTERNS.find((definition) => definition.patterns.some((pattern) => doesPathMatchPattern(path, pattern)));

export const isSuperAdminUser = (user) => SUPER_ADMIN_EMAILS.includes(user?.email);

export const getConfiguredAdminMenuKeys = (user) => (Array.isArray(user?.enabledMenus) ? user.enabledMenus : null);

const getResolvedConfiguredKeys = (user) => {
	const explicitKeys = getConfiguredAdminMenuKeys(user);
	return explicitKeys;
};

export const getInitialGymMenuKeys = (gym) => (Array.isArray(gym?.enabledMenus) ? gym.enabledMenus : DEFAULT_ENABLED_ADMIN_MENU_KEYS);

export const getVisibleAdminMenus = (user) => {
	const configuredKeys = getResolvedConfiguredKeys(user);

	if (!configuredKeys) {
		return MENUS;
	}

	return MENUS.filter((menu) => {
		if (!menu.accessKey) {
			return true;
		}

		const definition = CONFIGURED_MENU_LOOKUP[menu.accessKey];
		if (definition?.alwaysEnabled) {
			return true;
		}

		return configuredKeys.includes(menu.accessKey);
	});
};

export const canAccessAdminPath = (path, user) => {
	if (isSuperAdminUser(user)) {
		return true;
	}

	const configuredKeys = getResolvedConfiguredKeys(user);
	if (!configuredKeys) {
		return true;
	}

	const definition = getAccessDefinitionForPath(path);
	if (!definition) {
		return true;
	}

	const accessConfig = CONFIGURED_MENU_LOOKUP[definition.accessKey];
	if (accessConfig?.alwaysEnabled) {
		return true;
	}

	return configuredKeys.includes(definition.accessKey);
};

export const getAccessLabel = (accessKey, t) => {
	const definition = CONFIGURED_MENU_LOOKUP[accessKey];
	if (!definition) {
		return '';
	}

	return t(definition.labelKey);
};

export const getDeniedFeatureLabel = (path, t) => {
	const definition = getAccessDefinitionForPath(path);
	if (!definition) {
		return '';
	}

	return getAccessLabel(definition.accessKey, t);
};
